/**
 * Side effects of user-management gateway events: a poke goes onto the
 * sticky poke stack with its alert (pokes/stack.ts), and a security alert
 * tells the owner and Admins what happened. Both come only from the gateway.
 */
import { type GatewayDispatch, type GuildSecurityAlertKind } from '@neosocial/protocol';
import { tr } from '../i18n';
import { receivePoke } from '../pokes/stack';
import { toast } from '../ui/overlay';
import { useData } from './data';
import { userName } from './selectors';

/** The toast for each kind of NX_GUILD_SECURITY_ALERT the server sends. */
const ALERT_KEYS: Record<GuildSecurityAlertKind, string> = {
  channel_delete: 'um.alert.channelDelete',
  role_delete: 'um.alert.roleDelete',
  dangerous_permission_grant: 'um.alert.dangerousGrant',
  moderator_volume: 'um.alert.brake',
  owner_transfer_completed: 'um.alert.transferDone',
};

type Listener = (frame: GatewayDispatch) => void;
const listeners = new Set<Listener>();

/** Screens that show live lists (the audit log, invites) listen here. */
export function onGatewayEvent(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function onUmDispatch(frame: GatewayDispatch): void {
  for (const fn of [...listeners]) {
    try {
      fn(frame);
    } catch (e) {
      console.error(e);
    }
  }
  if (frame.t === 'NX_POKE') {
    // silent pokes (from someone you ignore, or while on Do Not Disturb) only go to the list
    receivePoke(frame.d);
    return;
  }
  if (frame.t === 'NX_GUILD_SECURITY_ALERT') {
    const d = frame.d;
    const { t } = tr();
    const s = useData.getState();
    const actor = d.actor_id ? userName(s, d.actor_id, d.guild_id) : '';
    const targetId = d.transfer?.to_user_id ?? d.target_id;
    const target = targetId ? userName(s, targetId, d.guild_id) : '';
    const key = ALERT_KEYS[d.kind];
    if (!key) return;
    // the transfer dialog has already said so to the one who did it
    if (d.kind === 'owner_transfer_completed' && d.actor_id && d.actor_id === s.me?.id) return;
    toast(t(key, { actor, target }), {
      icon: 'shield',
      tone: 'warn',
      duration: 12_000,
    });
  }
}
